<template>
  <div :class="{ 'employee-kpi-root': viewMode === 'my' || viewMode === 'team' || viewMode === 'org' }">
    <!-- Team/Org Summary strip — managers + org-wide approvers (same KPI chrome) -->
    <template v-if="showTeamSummaryStrip">
      <!-- Compact KPI strip — attendance + leave/reg approvals (Team + Org) -->
      <b-card
        class="mb-0 p-0 transparent-bg team-kpi-strip team-kpi-strip-6"
        body-class="p-0"
      >
        <b-row class="no-gutters employee-kpi-grid-row">
          <b-col lg class="mb-1 mb-lg-0 pr-lg-1 team-kpi-cell" cols="6">
            <div class="kpi-card-item">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-available">{{ teamSummary.total_members || displayedTeamMembersCount }}</h3>
                  <small class="text-muted kpi-label">{{ viewMode === 'org' ? 'Org Members' : 'Team Members' }}</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg class="mb-1 mb-lg-0 px-lg-1 team-kpi-cell" cols="6">
            <div class="kpi-card-item">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-present">{{ presentTodayCount }}</h3>
                  <small class="text-muted kpi-label">Present Today</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg class="mb-1 mb-lg-0 px-lg-1 team-kpi-cell" cols="6">
            <div class="kpi-card-item">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-pending">{{ absentTodayCount }}</h3>
                  <small class="text-muted kpi-label">Absent Today</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg class="mb-1 mb-lg-0 px-lg-1 team-kpi-cell" cols="6">
            <div class="kpi-card-item">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-wfh">{{ wfhTodayCount }}</h3>
                  <small class="text-muted kpi-label">WFH Today</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg class="mb-1 mb-lg-0 px-lg-1 team-kpi-cell" cols="6">
            <div
              class="kpi-card-item"
              :class="{ 'kpi-card-clickable': regularizationToApproveCount > 0 }"
              @click="handleRegToApproveClick"
            >
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-regularization">{{ regularizationToApproveCount }}</h3>
                  <small class="text-muted kpi-label">Reg. To Approve</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg class="mb-1 mb-lg-0 pl-lg-1 team-kpi-cell" cols="6">
            <div
              class="kpi-card-item"
              :class="{ 'kpi-card-clickable': leaveApprovalsToApproveCount > 0 }"
              @click="handleLeaveApprovalsClick"
            >
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-leave-approvals">{{ leaveApprovalsToApproveCount }}</h3>
                  <small class="text-muted kpi-label">Approve Leave</small>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </template>
    <!-- KPI Cards (for Employee View or when viewing specific team member) -->
    <!-- Uses kpiStats from new API or falls back to legacy displayKPIStats -->
    <template v-else>
      <b-card
        class="mb-0 p-0 transparent-bg"
        :class="{ 'employee-kpi-strip': viewMode === 'my', 'team-kpi-strip': viewMode === 'team' }"
        body-class="p-0"
      >
        <b-row class="no-gutters employee-kpi-grid-row">
          <b-col lg="3" md="3" sm="6" cols="6" class="mb-1 mb-md-0 pr-md-1 employee-kpi-cell team-kpi-cell">
            <div class="kpi-card-item">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-available" v-b-tooltip.hover.html="leaveBreakdownHtml || ''">{{ parseFloat(((kpiStats && kpiStats.leave_balance) || (displayKPIStats && displayKPIStats.leaveBalance) || 0).toFixed(1)) }}</h3>
                  <small class="text-muted kpi-label" v-b-tooltip.hover.html="leaveBreakdownHtml || ''">Leaves Remaining</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg="3" md="3" sm="6" cols="6" class="mb-1 mb-md-0 px-md-1 employee-kpi-cell">
            <div class="kpi-card-item">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-availed">{{ (kpiStats && kpiStats.leaves_taken) || (displayKPIStats && displayKPIStats.leavesTaken) || 0 }}</h3>
                  <small class="text-muted kpi-label">Leaves Taken</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg="3" md="3" sm="6" cols="6" class="mb-1 mb-md-0 px-md-1 employee-kpi-cell">
            <div class="kpi-card-item" :class="{'kpi-card-clickable': employeeApprovalPendingDisplay > 0}" @click="handleApprovalPendingClick">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-pending">{{ employeeApprovalPendingDisplay }}</h3>
                  <small class="text-muted kpi-label">{{ viewMode === 'team' || viewMode === 'org' ? 'Pending Approvals' : 'Approval Pending' }}</small>
                </div>
              </div>
            </div>
          </b-col>
          <b-col lg="3" md="3" sm="6" cols="6" class="mb-1 mb-md-0 pl-md-1 employee-kpi-cell">
            <div class="kpi-card-item">
              <div class="d-flex align-items-center justify-content-center">
                <div>
                  <h3 class="mb-0 kpi-number kpi-number-regularization">{{ (kpiStats && kpiStats.regularization_count) || (displayKPIStats && displayKPIStats.regularization) || 0 }}</h3>
                  <small class="text-muted kpi-label">Regularization</small>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  VBTooltip,
} from 'bootstrap-vue'

export default {
  name: 'AttendanceStats',
  components: {
    BRow,
    BCol,
    BCard,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    viewMode: {
      type: String,
      default: 'my',
    },
    canApplyOnBehalfAll: {
      type: Boolean,
      default: false,
    },
    canApplyOnBehalf: {
      type: Boolean,
      default: false,
    },
    selectedAccountManager: {
      type: [String, Number],
      default: null,
    },
    selectedTeamMember: {
      type: [String, Number],
      default: null,
    },
    selectedTeamWiseEmployee: {
      type: [String, Number],
      default: null,
    },
    canManageLeaves: {
      type: Boolean,
      default: false,
    },
    teamSummary: {
      type: Object,
      default: () => ({}),
    },
    displayedTeamMembersCount: {
      type: Number,
      default: 0,
    },
    pendingHRApprovalsLength: {
      type: Number,
      default: 0,
    },
    displayKPIStats: {
      type: Object,
      default: () => ({
        approvalPending: 0,
        leavesTaken: 0,
        leaveBalance: 0,
        regularization: 0,
      }),
    },
    upcomingTeamLeavesLength: {
      type: Number,
      default: 0,
    },
    outTodayCount: {
      type: Number,
      default: 0,
    },
    presentTodayCount: {
      type: Number,
      default: 0,
    },
    absentTodayCount: {
      type: Number,
      default: 0,
    },
    wfhTodayCount: {
      type: Number,
      default: 0,
    },
    regularizationToApproveCount: {
      type: Number,
      default: 0,
    },
    /** Pending leave approvals (same source as snapshot Leave approvals / pendingLeaveApprovalsForQueue) */
    leaveApprovalsToApproveCount: {
      type: Number,
      default: 0,
    },
    teamRegularizationCount: {
      type: Number,
      default: 0,
    },
    kpiStats: {
      type: Object,
      default: () => ({
        leave_balance: 0,
        leaves_taken: 0,
        approval_pending: 0,
        regularization_count: 0,
      }),
    },
    leaveBreakdownHtml: {
      type: String,
      default: '',
    },
  },
  computed: {
    /**
     * Team/Org KPI strip when viewing a whole team (not a single employee).
     * Includes account managers (canApplyOnBehalf) and org-wide roles (canApplyOnBehalfAll).
     */
    showTeamSummaryStrip() {
      if ((this.viewMode !== 'team' && this.viewMode !== 'org') || !this.canManageLeaves) return false
      if (this.selectedTeamMember || this.selectedTeamWiseEmployee) return false
      if (this.canApplyOnBehalfAll) {
        return !this.selectedAccountManager || !this.selectedTeamWiseEmployee
      }
      return !!this.canApplyOnBehalf
    },
    /**
     * Team/Org Pending Approvals KPI — prefer client-derived count
     * (aligned with Approve/Reject) over API team_summary.pending_approvals.
     */
    pendingApprovalsDisplay() {
      const fromDisplay = this.displayKPIStats && typeof this.displayKPIStats.approvalPending === 'number'
        ? this.displayKPIStats.approvalPending
        : null
      const fromSummary = this.teamSummary && typeof this.teamSummary.pending_approvals === 'number'
        ? this.teamSummary.pending_approvals
        : null
      if (fromDisplay != null && fromDisplay > 0) return fromDisplay
      if (fromSummary != null && fromSummary > 0) return fromSummary
      return fromDisplay != null ? fromDisplay : (fromSummary != null ? fromSummary : 0)
    },
    /**
     * Employee strip / team-manager strip: in team/org use the actionable count;
     * in my view prefer API kpi_stats then fall back to derived.
     */
    employeeApprovalPendingDisplay() {
      if (this.viewMode === 'team' || this.viewMode === 'org') {
        return this.pendingApprovalsDisplay
      }
      const fromKpi = Number(this.kpiStats && this.kpiStats.approval_pending) || 0
      const fromDisplay = Number(this.displayKPIStats && this.displayKPIStats.approvalPending) || 0
      return Math.max(fromKpi, fromDisplay)
    },
  },
  methods: {
    handleApprovalPendingClick() {
      this.$emit('approval-pending-click')
    },
    handleLeaveApprovalsClick() {
      if (!(this.leaveApprovalsToApproveCount > 0)) return
      this.$emit('approval-pending-click')
    },
    handleRegToApproveClick() {
      if (!(this.regularizationToApproveCount > 0)) return
      this.$emit('reg-to-approve-click')
    },
  },
}
</script>

<style lang="scss" scoped>
.transparent-bg {
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

.kpi-card-item {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
  padding: 0.625rem !important;
  text-align: center !important;
  height: 100% !important;
  min-height: 88px !important;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease !important;
}

.kpi-card-clickable {
  cursor: pointer !important;
}

.kpi-card-clickable:hover {
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.12) !important;
  border-color: #ff9800 !important;
}

.kpi-number {
  font-size: 1.5rem !important;
  font-weight: 700 !important;
  margin: 0 !important;
  line-height: 1.2 !important;
  font-family: inherit !important;
}

.kpi-number-available {
  color: #2196f3 !important;
}

.kpi-number-availed {
  color: #ff9800 !important;
}

.kpi-number-pending {
  color: #e67e22 !important;
}

.kpi-number-regularization {
  color: #f44336 !important;
}

.kpi-label {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #64748b !important;
  display: block !important;
  margin-top: 0.15rem !important;
}

.employee-kpi-root {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
}

.employee-kpi-strip {
  height: 100% !important;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.employee-kpi-strip :deep(.card-body),
.employee-kpi-strip.card .card-body {
  height: 100% !important;
  flex: 1 1 auto;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
}

.employee-kpi-strip :deep(.row),
.employee-kpi-strip .row {
  flex: 1 1 auto;
  height: 100%;
  align-items: stretch;
}

.employee-kpi-strip :deep(.row > [class*='col']),
.employee-kpi-strip .row > [class*='col'] {
  display: flex;
}

.employee-kpi-strip .kpi-card-item {
  min-height: 72px !important;
  height: 100% !important;
  width: 100%;
  padding: 0.7rem 0.45rem !important;
  border-radius: 10px !important;
  border-color: #e8ecf0 !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04) !important;
}

.employee-kpi-strip .kpi-number {
  font-size: 1.35rem !important;
}

.employee-kpi-strip .kpi-number-available {
  color: #0099AC !important;
}

.employee-kpi-strip .kpi-label {
  font-size: 0.65rem !important;
  letter-spacing: 0.04em !important;
  margin-top: 0.25rem !important;
}

/* Team/Org View KPI strip — same density as employee strip */
.team-kpi-strip {
  height: 100% !important;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.team-kpi-strip :deep(.card-body),
.team-kpi-strip.card .card-body {
  height: 100% !important;
  flex: 1 1 auto;
  padding: 0 !important;
  display: flex;
  flex-direction: column;
}

.team-kpi-strip :deep(.row),
.team-kpi-strip .row {
  flex: 1 1 auto;
  height: 100%;
  align-items: stretch;
}

.team-kpi-strip :deep(.row > [class*='col']),
.team-kpi-strip .row > [class*='col'] {
  display: flex;
}

.team-kpi-strip .kpi-card-item {
  min-height: 72px !important;
  height: 100% !important;
  width: 100%;
  padding: 0.55rem 0.35rem !important;
  border-radius: 10px !important;
  border-color: #e8ecf0 !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04) !important;
}

.team-kpi-strip .kpi-number {
  font-size: 1.25rem !important;
}

.team-kpi-strip .kpi-number-available {
  color: #0099AC !important;
}

.team-kpi-strip .kpi-number-present {
  color: #0099AC !important;
}

.team-kpi-strip .kpi-number-wfh {
  color: #5b8fa8 !important;
}

.team-kpi-strip .kpi-number-pending {
  color: #6b7280 !important;
}

.team-kpi-strip .kpi-number-regularization {
  color: #8a6d3b !important;
}

.team-kpi-strip .kpi-number-leave-approvals {
  color: #c2410c !important;
}

.team-kpi-strip .kpi-card-clickable:hover {
  box-shadow: 0 2px 8px rgba(0, 153, 172, 0.1) !important;
  border-color: #0099AC !important;
}

.team-kpi-strip .kpi-label {
  font-size: 0.65rem !important;
  letter-spacing: 0.04em !important;
  margin-top: 0.25rem !important;
}

/* 6-card Team/Org strip — denser labels, equal flex columns on lg+ */
.team-kpi-strip-6 .kpi-label {
  font-size: 0.55rem !important;
  line-height: 1.2;
  white-space: normal;
}

.team-kpi-strip-6 .kpi-number {
  font-size: 1.1rem !important;
}

.team-kpi-strip-6 .kpi-card-item {
  min-height: 72px !important;
  padding: 0.5rem 0.2rem !important;
}

.team-kpi-strip .kpi-sublabel {
  display: block;
  font-size: 0.58rem !important;
  font-weight: 600;
  color: #0099AC !important;
  margin-top: 0.1rem;
  letter-spacing: 0.02em;
  text-transform: none;
}

@media (max-width: 991px) {
  .employee-kpi-root .team-kpi-strip {
    height: auto !important;
  }

  .employee-kpi-root .team-kpi-strip :deep(.card-body),
  .employee-kpi-root .team-kpi-strip.card .card-body {
    height: auto !important;
    padding: 0 !important;
  }

  .employee-kpi-root .team-kpi-strip :deep(.row),
  .employee-kpi-root .team-kpi-strip .row {
    height: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 0.625rem;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .employee-kpi-root .team-kpi-strip :deep(.row > [class*='col']),
  .employee-kpi-root .team-kpi-strip .row > [class*='col'],
  .employee-kpi-root .team-kpi-strip .team-kpi-cell {
    flex: 0 0 calc(50% - 0.3125rem);
    max-width: calc(50% - 0.3125rem);
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-bottom: 0 !important;
  }

  .employee-kpi-root .team-kpi-strip .kpi-card-item {
    min-height: 68px !important;
    height: auto !important;
    width: 100%;
    border-radius: 10px !important;
  }

  .employee-kpi-root .team-kpi-strip-6 .kpi-label {
    font-size: 0.58rem !important;
    line-height: 1.25;
  }

  .employee-kpi-root .team-kpi-strip .kpi-label {
    white-space: normal;
    line-height: 1.25;
  }
}

@media (max-width: 575px) {
  .employee-kpi-root .team-kpi-strip :deep(.row),
  .employee-kpi-root .team-kpi-strip .row {
    gap: 0.5rem;
  }

  .employee-kpi-root .team-kpi-strip :deep(.row > [class*='col']),
  .employee-kpi-root .team-kpi-strip .row > [class*='col'],
  .employee-kpi-root .team-kpi-strip .team-kpi-cell {
    flex: 0 0 calc(50% - 0.25rem);
    max-width: calc(50% - 0.25rem);
  }

  .employee-kpi-root .team-kpi-strip .kpi-card-item {
    min-height: 64px !important;
    padding: 0.45rem 0.35rem !important;
  }

  .employee-kpi-root .team-kpi-strip .kpi-number {
    font-size: 1.1rem !important;
  }

  .employee-kpi-root .team-kpi-strip-6 .kpi-number {
    font-size: 1.05rem !important;
  }

  .employee-kpi-root .team-kpi-strip-6 .kpi-label {
    font-size: 0.54rem !important;
  }
}

/* Phone / small tablet: readable 2×2 KPI grid with consistent gutters */
@media (max-width: 991px) {
  .employee-kpi-root {
    height: auto;
  }

  .employee-kpi-root .employee-kpi-strip {
    height: auto !important;
  }

  .employee-kpi-root .employee-kpi-strip :deep(.card-body),
  .employee-kpi-root .employee-kpi-strip.card .card-body {
    height: auto !important;
  }

  .employee-kpi-root .employee-kpi-strip :deep(.row),
  .employee-kpi-root .employee-kpi-strip .row {
    height: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 0.625rem;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .employee-kpi-root .employee-kpi-strip :deep(.row > [class*='col']),
  .employee-kpi-root .employee-kpi-strip .row > [class*='col'],
  .employee-kpi-root .employee-kpi-strip .employee-kpi-cell {
    flex: 0 0 calc(50% - 0.3125rem);
    max-width: calc(50% - 0.3125rem);
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-bottom: 0 !important;
  }

  .employee-kpi-root .employee-kpi-strip .kpi-card-item {
    min-height: 76px !important;
    height: auto !important;
    width: 100%;
    padding: 0.65rem 0.4rem !important;
  }

  .employee-kpi-root .employee-kpi-strip .kpi-number {
    font-size: 1.35rem !important;
  }

  .employee-kpi-root .employee-kpi-strip .kpi-label {
    font-size: 0.68rem !important;
    letter-spacing: 0.03em !important;
    white-space: normal;
    line-height: 1.25;
  }
}

@media (max-width: 575px) {
  .employee-kpi-root .employee-kpi-strip :deep(.row),
  .employee-kpi-root .employee-kpi-strip .row {
    gap: 0.5rem;
  }

  .employee-kpi-root .employee-kpi-strip :deep(.row > [class*='col']),
  .employee-kpi-root .employee-kpi-strip .row > [class*='col'],
  .employee-kpi-root .employee-kpi-strip .employee-kpi-cell {
    flex: 0 0 calc(50% - 0.25rem);
    max-width: calc(50% - 0.25rem);
  }

  .employee-kpi-root .employee-kpi-strip .kpi-card-item {
    min-height: 72px !important;
    padding: 0.55rem 0.35rem !important;
  }

  .employee-kpi-root .employee-kpi-strip .kpi-number {
    font-size: 1.25rem !important;
  }

  .employee-kpi-root .employee-kpi-strip .kpi-label {
    font-size: 0.62rem !important;
  }
}

.kpi-button {
  margin-top: 0.4rem !important;
  width: 100% !important;
  padding: 0.25rem 0.5rem !important;
  font-size: 0.75rem !important;
}

.team-summary-card {
  background: #ffffff;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
  padding: 0.625rem !important;
  text-align: center !important;
  height: 100% !important;
  flex-direction: column !important;
  justify-content: center !important;
  align-items: center !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.team-summary-number {
  font-size: 1.5rem !important;
  font-weight: 700 !important;
  margin: 0 !important;
  line-height: 1.2 !important;
  font-family: inherit !important;
}

.team-summary-number-green {
  color: #4caf50 !important;
}

.team-summary-number-orange {
  color: #ff9800 !important;
}

.team-summary-number-blue {
  color: #2196f3 !important;
}

.team-summary-number-red {
  color: #f44336 !important;
}

.team-summary-label {
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #64748b !important;
  display: block !important;
  margin-top: 0.15rem !important;
}

/* Responsive adjustments for KPI cards - matching original */
@media (min-width: 1440px) {
  .kpi-card-item {
    padding: 0.75rem !important;
  }
  
  .kpi-number {
    font-size: 1.625rem !important;
  }
  
  .kpi-label {
    font-size: 0.8rem !important;
  }
}

@media (min-width: 992px) and (max-width: 1439px) {
  .kpi-card-item {
    padding: 0.625rem !important;
  }
  
  .kpi-number {
    font-size: 1.4rem !important;
  }
  
  .kpi-label {
    font-size: 0.7rem !important;
  }
}

@media (min-width: 768px) and (max-width: 991px) {
  .kpi-card-item {
    padding: 0.5rem !important;
  }
  
  .kpi-number {
    font-size: 1.25rem !important;
  }
  
  .kpi-label {
    font-size: 0.65rem !important;
  }
}

@media (min-width: 576px) and (max-width: 767px) {
  .kpi-card-item {
    padding: 0.5rem !important;
  }
  
  .kpi-number {
    font-size: 1.15rem !important;
  }
  
  .kpi-label {
    font-size: 0.6rem !important;
  }
}

@media (max-width: 575px) {
  .kpi-card-item {
    padding: 0.5rem !important;
  }
  
  .kpi-number {
    font-size: 1.1rem !important;
  }
  
  .kpi-label {
    font-size: 0.6rem !important;
  }
}

/* Team summary responsive (unchanged) */
@media (max-width: 768px) {
  .team-summary-card {
    padding: 0.5rem !important;
  }

  .team-summary-number {
    font-size: 1.25rem !important;
  }

  .team-summary-label {
    font-size: 0.65rem !important;
  }
}

@media (min-width: 992px) and (max-width: 1439px) {
  .team-summary-card {
    padding: 0.625rem !important;
  }
  
  .team-summary-number {
    font-size: 1.4rem !important;
  }
  
  .team-summary-label {
    font-size: 0.7rem !important;
  }
}

@media (min-width: 1440px) {
  .team-summary-card {
    padding: 0.75rem !important;
  }
  
  .team-summary-number {
    font-size: 1.625rem !important;
  }
  
  .team-summary-label {
    font-size: 0.8rem !important;
  }
}
</style>

<style lang="scss">
/* Unscoped: Bootstrap b-row/b-col need global rules; scoped :deep() did not override col-6 */
@media (max-width: 991px) {
  .employee-kpi-root .employee-kpi-grid-row {
    display: flex !important;
    flex-wrap: wrap !important;
    gap: 0.625rem !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .employee-kpi-root .employee-kpi-grid-row > [class*='col-'] {
    flex: 0 0 calc(50% - 0.3125rem) !important;
    max-width: calc(50% - 0.3125rem) !important;
    width: auto !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-bottom: 0 !important;
  }
}

@media (max-width: 575px) {
  .employee-kpi-root .employee-kpi-grid-row {
    gap: 0.5rem !important;
  }

  .employee-kpi-root .employee-kpi-grid-row > [class*='col-'] {
    flex: 0 0 calc(50% - 0.25rem) !important;
    max-width: calc(50% - 0.25rem) !important;
  }
}

/* Leave balance KPI tooltip — transposed table (leave types × metrics) */
.tooltip .tooltip-inner:has(.leave-kpi-tooltip) {
  max-width: 520px !important;
  text-align: left !important;
  overflow: visible !important;
}

.tooltip .leave-kpi-tooltip {
  overflow: visible !important;
}

.tooltip .leave-kpi-tooltip table {
  width: 100%;
  border-collapse: collapse;
}

.tooltip .leave-kpi-tooltip th,
.tooltip .leave-kpi-tooltip td {
  box-sizing: border-box;
}
</style>